<script lang="ts" setup>
import { BsError, useToast } from '@afo/bsf-core'
import useEsgContactForm from '@/domains/esg/composables/useEsgContactForm'
import useEsgContactApi from '@/domains/esg/api/useEsgContactApi'
import Recaptcha from '@/domains/recaptcha/components/Recaptcha/Recaptcha.vue'
import Rodo from '@/components/Rodo/Rodo.vue'
import { useRecaptcha } from '@/domains/recaptcha/composables/useRecaptcha'
import TextInput from '@/components/Inputs/TextInput/TextInput.vue'
import type { IProps } from '@/domains/esg/components/ContactForm/ContactForm.types'
import FileController from '@/components/FileController/FileController.vue'
import { useRouteLink } from '@/composables/useRouteLink'
import { ROUTE_PATHS } from '@/utils/route'

const { findRouteName } = useRouteLink()
const fallbackRedirectTo = findRouteName(ROUTE_PATHS.ABOUT_RESPONSIBLE_BUSINESS_THANKYOU)

const props = withDefaults(defineProps<IProps>(), {
  color: 'white',
  header: '',
  badge: '',
  formId: '',
  redirectTo: '',
  image: '/images/image_contact.jpg',
})

const router = useRouter()
const localePath = useLocalePath()
const config = useRuntimeConfig().public
const { $v, form } = useEsgContactForm()
const { submitContactForm } = useEsgContactApi()
const { recaptchaValid } = useRecaptcha()
const toast = useToast()
const { t }= useI18n()

async function submitForm() {
  if (recaptchaValid.value) return

  const isValid = await $v.value.$validate()
  if (!isValid) return

  try {
    const { firstName, lastName, email, messageTitle, messageText, document } = form

    const data = {
      firstName,
      lastName,
      email,
      messageTitle,
      messageText,
      document,
    }

    await submitContactForm(data)
    const routeToRedirect = props.redirectTo || fallbackRedirectTo
    await router.push(localePath(routeToRedirect))
  } catch (error) {
    console.log(error)
    toast.error(t('error.header500'))
  }
}
</script>

<template>
  <UiSection
    id="formularz"
    :color="color"
    >
    <div class="form">
      <div class="form__content">
        <h2
          v-if="header"
          class="form__header"
          v-html="header"
        />

        <div class="form__image">
          <span
            v-if="badge"
            class="form__image_badge"
            :style="{ backgroundImage: `url(${badge})` }"
          />

          <img
            :src="image"
            :alt="$t('responsibleBusiness.contact.form.header')"
          >
        </div>
      </div>

      <div class="form__form">
        <form
          :action="config.formApiLink"
          method="POST"
          @submit.prevent="submitForm"
        >
          <div class="form__form_row">
            <div class="form__half">
              <TextInput
                id="firstName"
                v-model="form.firstName"
                name="firstName"
                :label="$t('responsibleBusiness.contact.form.fields.firstName')"
                :error-message="$v.firstName.$errors[0]?.$message"
                :error="!!$v.firstName.$errors[0]?.$message"
                @blur="$v.firstName.$touch()"
              />
            </div>

            <div class="form__half">
              <TextInput
                id="lastName"
                v-model="form.lastName"
                name="lastName"
                :label="$t('responsibleBusiness.contact.form.fields.lastName')"
                :error-message="$v.lastName.$errors[0]?.$message"
                :error="!!$v.lastName.$errors[0]?.$message"
                @blur="$v.lastName.$touch()"
              />
            </div>
          </div>

          <div class="form__full">
            <TextInput
              id="email"
              v-model="form.email"
              name="email"
              :label="$t('responsibleBusiness.contact.form.fields.email')"
              :error-message="$v.email.$errors[0]?.$message"
              :error="!!$v.email.$errors[0]?.$message"
              :required="true"
              @blur="$v.email.$touch()"
            />
          </div>

          <div class="form__full">
            <TextInput
              id="messageTitle"
              v-model="form.messageTitle"
              name="messageTitle"
              :label="$t('responsibleBusiness.contact.form.fields.messageTitle')"
              :error-message="$v.messageTitle.$errors[0]?.$message"
              :error="!!$v.messageTitle.$errors[0]?.$message"
              :required="true"
              @blur="$v.messageTitle.$touch()"
            />
          </div>

          <div class="form__full">
            <BsError :message="$v.messageText.$errors[0]?.$message">
              <label
                for="messageText"
                class="bs-input__label"
                :class="{ 'label--error': !!$v.messageText.$errors[0]?.$message }"
              >
                {{ $t('responsibleBusiness.contact.form.fields.messageText') }}
              </label>

              <div
                :class="[
                  'form__form_input form__form_input--textarea',
                  { error: !!$v.messageText.$errors[0]?.$message },
                ]"
              >
                <textarea
                  id="messageText"
                  v-model="form.messageText"
                  name="messageText"
                  @blur="$v.messageText.$touch()"
                />
              </div>
            </BsError>
          </div>

          <div class="form__full">
            <BsError :message="$v.document.$errors[0]?.$message">
              <FileController
                id="document"
                v-model="form.document"
                :error="!!$v.document.$errors[0]?.$message"
                name="document"
                :label="$t('responsibleBusiness.contact.form.fields.file')"
                @update:model-value="$v.document.$touch()"
                />
            </BsError>
          </div>

          <div class="form__form_captcha">
            <Recaptcha>
              <input
                class="form__form_button"
                type="submit"
                :value="$t('form.submit')"
                :disabled="recaptchaValid"
              >
            </Recaptcha>
          </div>
        </form>

        <Rodo
          :text-short="$t('responsibleBusiness.contact.rodo.notExpand')"
          :text-expanded="$t('responsibleBusiness.contact.rodo.expand')"
        />

      </div>
    </div>
  </UiSection>
</template>

<style lang="scss" scoped>

#formularz {
  display: none !important;
}

.form {
  $root: &;

  display: flex;
  flex-direction: column;

  &__header {
    @include h8;

    :deep(small) {
      margin-top: 0.8rem;
      @include body-1;
    }

    :deep(span) {
      color: var(--span-color);
    }
  }

  &__image {
    position: relative;
    width: 100%;
    height: 24rem;
    margin-top: 4rem;
    margin-bottom: 3.2rem;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
      border-radius: 1.6rem;
    }

    &_badge {
      position: absolute;
      top: -4.8rem;
      right: -1.6rem;
      z-index: 2;
      display: inline-block;
      width: 9.6rem;
      height: 9.6rem;
      background-repeat: no-repeat;
      background-position: center;
      background-size: cover;
    }
  }

  &__form {
    :deep(.bs-input__input, .bs-select__toggler) {
      background-color: $white;
    }

    :deep(.bs-input__label, .bs-select__label) {
      font-weight: 400;
    }

    .bs-error {
      margin-bottom: 0.8rem;
    }

    .form__full {
      clear: both;
      width: 100%;
      margin-top: 1.6rem;

      .bs-error,
      .bs-input,
      .bs-select {
        width: 100%;
      }
    }

    &_row {
      display: flex;
      flex-direction: column;
      gap: 1.6rem;
      margin-top: 1.6rem;

      &:first-of-type {
        margin-top: 0;
      }

      .form__half {
        float: none;
        width: 100%;
        margin: 0;

        :deep(label, .bs-input__label, .bs-select__label)
        {
          white-space: nowrap;
        }

        :deep(.bs-error, .bs-input, .bs-select) {
          width: 100%;
        }
      }
    }

    .form__half {
      #{$root}__form_input {
        margin-bottom: 0;
      }
      & > span.error {
        display: inline-block;
        margin-top: 0.8rem;
        color: red;
      }
    }

    label {
      display: inline-block;
      margin-bottom: 0.4rem;
      margin-left: 0.4rem;
      color: $text-300;
      @include body-3;

      &.label--error {
        color: $invalid-500;
      }
    }

    &_input {
      position: relative;
      width: 100%;
      max-height: 4rem;
      height: 4rem;
      margin-bottom: 1.6rem;
      background-color: $white;
      border: 0.1rem solid $gray-400;
      border-radius: 1.6rem;
      transition: border-color 0.2s;

      &:hover {
        border-color: $text-400;
      }

      &.error {
        border: 0.1rem solid red;
      }

      & > .error {
        display: inline-block;
        margin-top: 0.8rem;
        color: red;
      }

      &--textarea {
        max-height: 9.6rem;
        height: 9.6rem;
        margin-bottom: 0;
      }

      input,
      select,
      textarea {
        @include body-2;
        width: 100%;
        height: 100%;
        padding: 0.8rem 1.6rem;
        background-color: transparent;

        &::-webkit-input-placeholder {
          color: $text-200;
        }

        &:-ms-input-placeholder {
          color: $text-200;
        }

        &::placeholder {
          color: $text-200;
        }
      }

      select {
        -moz-appearance: none;
        -webkit-appearance: none;
        appearance: none;
      }
    }

    &_button {
      @include body-3-medium;
      display: inline-block;
      min-width: 8.6rem;
      width: auto;
      padding: 1.4rem 2.4rem;
      color: $white;
      text-align: center;
      background-color: $text-400;
      border-radius: 5rem;
      cursor: pointer;

      &:disabled {
        background-color: $gray-400;
        cursor: not-allowed;
      }
    }
  }
}

:deep(.fileController__label) {
  display: flex;
}

@include atXS {
  .form__image {
    height: 54rem;
  }

  .form__form_row {
    flex-direction: row;
    align-items: flex-start;
  }

  .form__form_row .form__half {
    flex: 1 1 0;
    width: auto;
  }
}

@include atS {
  .form {
    flex-direction: row;
    gap: 6.4rem;
    max-width: 115.2rem;
    margin: 0 auto;
  }

  .form__content,
  .form__form {
    min-width: 0;
    width: 100%;
  }

  .form__image_badge {
    right: -4.8rem;
  }
}

@include atM {
  .section {
    margin: 2.4rem;
    padding: 12rem 2.4rem;
  }

  .form {
    gap: 13rem;
  }

  .form__header {
    @include h6;

    :deep(small) {
      @include h8;
    }
  }

  .form__image {
    margin-top: 10.7rem;
    margin-bottom: 0;
  }

  .form__image_badge {
    top: -7.3rem;
    right: -7.3rem;
    width: 14.7rem;
    height: 14.7rem;
  }

  .form__form_input {
    max-height: 4.8rem;
    height: 4.8rem;
  }

  .form__form_input--textarea {
    max-height: 10.2rem;
    height: 10.2rem;
  }

  .form__form_input input,
  .form__form_input select,
  .form__form_input textarea {
    padding: 1.2rem 1.6rem;
  }

  .form__form_captcha {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }
}

@include atXL {
  .form {
    max-width: 134rem;
  }
}
</style>
